import { useEffect, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { AdminPermissionsProvider, useAdminPermissions } from '../../contexts/AdminPermissionsContext'
import { Icon } from '../ui/Icons'
import InstallAppButton from '../pwa/InstallAppButton'
import { usePwaInstall } from '../../hooks/usePwaInstall'

const roleLabels = { admin: 'Administrador', directiva: 'Directiva', socio: 'Socio' }
const navigation = [
  { label: 'Dashboard', icon: 'dashboard', to: '/dashboard', enabled: true },
  { label: 'Socios', icon: 'users', to: '/socios', enabled: true, roles: ['admin', 'directiva'] },
  { label: 'Alumnos', icon: 'student' },
  { label: 'Cursos', icon: 'course', to: '/cursos', enabled: true, roles: ['admin', 'directiva'] },
  { label: 'Consultas', icon: 'message', to: '/consultas', enabled: true, roles: ['admin', 'directiva'] },
  { label: 'Tesorería', icon: 'wallet', to: '/tesoreria', enabled: true, roles: ['admin', 'directiva'] },
  { label: 'Beneficios', icon: 'gift' },
  { label: 'Usuarios', icon: 'userShield', to: '/usuarios', enabled: true, permiso: 'gestionar_usuarios' },
]

function Sidebar({ close, installState }) {
  const { authUser, organizacion, rol, signOut } = useAuth()
  const { puedeGestionarUsuarios } = useAdminPermissions()
  const canSee = (item) => (!item.roles || item.roles.includes(rol)) && (!item.permiso || (item.permiso === 'gestionar_usuarios' && puedeGestionarUsuarios))
  const [logoutError, setLogoutError] = useState('')

  async function handleSignOut() {
    setLogoutError('')
    try { await signOut() } catch (error) { setLogoutError(error.message) }
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto overscroll-contain bg-primary pt-safe pb-safe text-white">
      <div className="flex min-h-24 shrink-0 items-center gap-3 border-b border-white/15 py-3 pl-5 pr-14 lg:pr-5">
        <div className="flex h-16 w-20 shrink-0 items-center justify-center rounded-xl bg-white p-1.5"><img src="/logo-cpa.jpg" alt="Centro de Padres y Apoderados CSPAH" className="h-full w-full object-contain" /></div>
        <div className="min-w-0"><p className="truncate font-semibold">{organizacion.nombre}</p><p className="truncate text-xs text-white/70">{organizacion.colegio || 'Centro de Padres'}</p></div>
      </div>
      <nav className="flex-1 space-y-1 px-3 py-5" aria-label="Navegación principal">
        {navigation.filter(canSee).map((item) => item.enabled ? (
          <NavLink key={item.label} to={item.to} onClick={close} className={({ isActive }) => `flex items-center gap-3 rounded-xl border-l-4 px-3 py-3 text-sm font-medium transition ${isActive ? 'border-secondary bg-white/15 text-white' : 'border-transparent text-white/80 hover:bg-white/10 hover:text-white'}`}>
            <Icon name={item.icon} />{item.label}
          </NavLink>
        ) : (
          <div key={item.label} className="flex cursor-not-allowed items-center gap-3 rounded-xl border-l-4 border-transparent px-3 py-3 text-sm text-white/45" title="Próximamente">
            <Icon name={item.icon} />{item.label}<span className="ml-auto rounded-full bg-white/5 px-2 py-0.5 text-[10px] uppercase">Pronto</span>
          </div>
        ))}
      </nav>
      <div className="border-t border-white/10 p-3">
        <div className="mb-3 rounded-xl bg-white/5 p-3">
          <p className="truncate text-sm font-medium">{authUser.email}</p><p className="mt-1 text-xs text-white/75">{roleLabels[rol]}</p>
        </div>
        <button disabled className="flex min-h-11 w-full cursor-not-allowed items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-white/45"><Icon name="settings" />Configuración<span className="ml-auto text-[10px] uppercase">Pronto</span></button>
        <InstallAppButton installState={installState} />
        <button onClick={handleSignOut} className="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-white/80 transition hover:bg-danger hover:text-white"><Icon name="logout" />Cerrar sesión</button>
        {logoutError && <p className="px-3 pt-2 text-xs text-red-100" role="alert">{logoutError}</p>}
      </div>
    </div>
  )
}

export default function AppShell() {
  return <AdminPermissionsProvider><AppShellLayout /></AdminPermissionsProvider>
}

function AppShellLayout() {
  const [open, setOpen] = useState(false)
  const { organizacion } = useAuth()
  const installState = usePwaInstall()

  // Con el menú abierto en móvil, el contenido de fondo no debe desplazarse.
  useEffect(() => {
    if (!open) return undefined
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [open])
  return (
    <div className="min-h-dvh bg-background">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-72 lg:block"><Sidebar installState={installState} /></aside>
      {open && <div className="fixed inset-0 z-40 bg-slate-950/60 backdrop-blur-sm lg:hidden" onClick={() => setOpen(false)} />}
      <aside className={`fixed inset-y-0 left-0 z-50 w-[min(18rem,85vw)] transform transition-[transform,visibility] duration-200 lg:hidden ${open ? 'translate-x-0' : '-translate-x-full invisible'}`} aria-hidden={!open}>
        <Sidebar close={() => setOpen(false)} installState={installState} />
        <button onClick={() => setOpen(false)} className="absolute right-2 top-[calc(0.75rem+env(safe-area-inset-top,0px))] grid h-11 w-11 place-items-center rounded-xl text-white/80 hover:bg-white/10" aria-label="Cerrar menú"><Icon name="close" /></button>
      </aside>
      <div className="lg:pl-72">
        <header className="sticky top-0 z-20 border-b border-border bg-surface/95 pt-safe backdrop-blur"><div className="flex h-16 items-center px-4 sm:h-20 sm:px-8">
          <button onClick={() => setOpen(true)} className="mr-3 grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-border text-primary transition hover:border-secondary hover:bg-secondary/5 lg:hidden" aria-label="Abrir menú" aria-expanded={open}><Icon name="menu" /></button>
          <img src="/logo-cpa-transparent.png" alt="Centro de Padres y Apoderados CSPAH" width="625" height="777" className="mr-3 h-10 w-auto shrink-0 object-contain lg:hidden" />
          <div className="min-w-0"><p className="text-xs font-semibold uppercase tracking-widest text-primary">Panel de gestión</p><h1 className="truncate text-base font-semibold text-text-primary sm:text-lg">{organizacion.nombre}</h1></div>
        </div></header>
        <main className="px-4 pt-5 pb-safe-page sm:px-8 sm:pt-8"><Outlet /></main>
      </div>
    </div>
  )
}
